import styles from './period-churn-report.module.scss';
import { useContext, useState } from 'react';
import { useGetRequestNewHook, useMakeRequestHook, useTableConfigHook } from '../requestUtils';
import { Col, Row } from 'reactstrap';
import { ValueFormatterParams } from 'ag-grid-community';
import { ContactListContext } from '../customer-context/customer-context';
import { Box, CreateHeaders, HeaderSizeEnum, Tab, Tabs, currencyHeader, textHeader, DatePicker, Button, TextField, toCurrency, TableFilters } from '@frontend-workspace/react-shared-lib';
import PeriodChurnReportSkeleton from './period-churn-report-skeleton';
import { IRelationProduct } from '../_models/relation-product';
import { IMonthlyChurnReport } from '../_models/monthly-churn-report';
import { WebkuaProductSelect, WebkuaTable } from '@frontend-workspace/webkua-lib';
import { addDays, addMonths } from 'date-fns';
import { useTranslation } from 'react-i18next';


/* eslint-disable-next-line */
export interface PeriodChurnReportProps { }

export function PeriodChurnReport(props: PeriodChurnReportProps) {
  const { t } = useTranslation();
  const contactList = useContext(ContactListContext);
  const makeRequest = useMakeRequestHook();
  const tableConfig = useTableConfigHook("periodChurnReport");
  const { data: products }: { data: IRelationProduct[] } = useGetRequestNewHook(`relationProducts`, []);

  const productOptions = products.map(p => ({ label: `${p.productName}`, value: p.id }));
  const [startDate, setStartDate] = useState<Date>(addMonths(new Date(), -2));
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [includeAcademic, setincludeAcademic] = useState<boolean>(false);
  const [includeRestructuring, setIncludeRestructuring] = useState<boolean>(true);
  const [includeFree, setIncludeFree] = useState<boolean>(false);
  const [includeNonAcademic, setIncludeNonAcademic] = useState<boolean>(true);
  const [includeComercial, setIncludeComercial] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [endDate, setEndDate] = useState<Date>(addMonths(new Date(), -1));
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [unpackSeatsPrQuantity, setUnpackSeatsPrQuantity] = useState<boolean>(true);
  const [currentMonth, setCurrentMonth] = useState<IMonthlyChurnReport>();

  const selectTerminatedItems = () => setSelectedTab(0);
  const selectNewItems = () => setSelectedTab(1);
  const selectChangedItems = () => setSelectedTab(2);

  const handleStartDateChange = (date: Date | null) => date && setStartDate(addDays(date, 1));
  const handleEndDateChange = (date: Date | null) => date && setEndDate(addDays(date, 1));

  const getReport = () => {
    setIsLoading(true);
    makeRequest(`reports/period-churn`, "PUT", {
      fromTime: startDate,
      untilTime: endDate,
      includeAcademic,
      includeComercial,
      includeNonAcademic,
      includeFree,
      includeRestructuring,
      unpackSeatsPrQuantity,
      productIds: selectedProductIds
    })
      .then((data: IMonthlyChurnReport) => {
        setCurrentMonth(data);
        setIsLoading(false);
      }).catch(exception => {
        setIsLoading(false);
        console.log(exception);
      })
  };

  const companyNameRenderer = (params: ValueFormatterParams) => {
    const contact = contactList.find(c => c.number === params.value);
    return `${contact?.name} ${contact?.department}`;
  };

  const selectedItems = currentMonth && !isLoading ?
    selectedTab === 0 ? currentMonth.lostItems :
      selectedTab === 1 ? currentMonth.newItems :
        currentMonth.changedItems : [];

  const headers = CreateHeaders([
    textHeader("companyNumber", t("standard.company"), HeaderSizeEnum.LARGE),
    textHeader("companyNumber", t("standard.companyName"), HeaderSizeEnum.LARGE, { renderer: companyNameRenderer }),
    textHeader("licenseNumber", t("standard.licenseNumber"), HeaderSizeEnum.MEDIUM),
    textHeader("licenseName", t("standard.licenseName"), HeaderSizeEnum.EXTRA_LARGE),
    textHeader("quantity", t("standard.quantity"), HeaderSizeEnum.SMALL, {
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    textHeader("itemInfoId", t("standard.item"), HeaderSizeEnum.LARGE),
    currencyHeader("monthlyValue", t("periodChurnReport.monthlyValue"), HeaderSizeEnum.LARGE, {
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    }),
    currencyHeader("monthlyValue", t("periodChurnReport.annualValue"), HeaderSizeEnum.LARGE, {
      renderer: (params: ValueFormatterParams) => {
        return toCurrency(params.value * 12)
      },
      cellStyle: { justifyContent: 'right' },
      headerClass: "ag-right-aligned-header"
    })
  ])

  return <>
    <div className='d-flex flex-wrap'>
      <div className='pe-3 mb-3'>
        <WebkuaProductSelect
          relationProducts={products}
          selectedProductIds={selectedProductIds}
          setSelectedProductIds={setSelectedProductIds}
        />
      </div>
      <div className='me-3 mb-3'>
        <DatePicker
          inputFormat="MM.yyyy"
          value={startDate}
          onChange={handleStartDateChange}
          openTo="month"
          views={['year', 'month']}
          label={t("standard.from")}
        />
      </div>
      <div className='mb-3'>
        <DatePicker
          inputFormat="MM.yyyy"
          value={endDate}
          onChange={handleEndDateChange}
          openTo="month"
          views={['year', 'month']}
          label={t("standard.to")}
        />
      </div>
    </div>

    <div className='mb-3'>
      <TableFilters switches={[
        // {
        //   label: "Kommersielle",
        //   checked: includeComercial,
        //   onChange: setIncludeComercial
        // },
        {
          label: t("standard.nonAcademic"),
          checked: includeNonAcademic,
          onChange: setIncludeNonAcademic
        },
        // {
        //   label: "Ikke akademiske",
        //   checked: includeNonAcademic,
        //   onChange: setIncludeNonAcademic
        // },
        {
          label: t("standard.academic"),
          checked: includeAcademic,
          onChange: setincludeAcademic
        },
        // {
        //   label: "Gratislisenser",
        //   checked: includeFree,
        //   onChange: setIncludeFree
        // },
        {
          label: t("standard.unpackSeats"),
          checked: unpackSeatsPrQuantity,
          onChange: setUnpackSeatsPrQuantity
        },
        {
          label: t("standard.includeRestructuring"),
          checked: includeRestructuring,
          onChange: setIncludeRestructuring
        }
      ]} />
    </div>

    <div className='mb-5'>
      <Button
        onClick={getReport}
        className="mt-3"
        variant="contained">
        {t("periodChurnReport.retrieveData")}
      </Button>
    </div>

    {isLoading ?
      <PeriodChurnReportSkeleton /> : <>
        {currentMonth && <Row>
          <Col md={6} xl={3} xxl={2}>
            <TextField
              label={t("standard.companyChurn")}
              className='mb-3'
              fullWidth
              disabled
              value={currentMonth.companyChurn}
            />
            <TextField
              label={t("standard.companyGrowth")}
              className='mb-3'
              fullWidth
              disabled
              value={currentMonth.companyGrowth}
            />
          </Col>
          <Col md={6} xl={3} xxl={2}>
            <TextField
              label={t("periodChurnReport.seatChurn")}
              className='mb-3'
              fullWidth
              disabled
              value={currentMonth.seatChurn}
            />
            <TextField
              label={t("periodChurnReport.seatGrowth")}
              className='mb-3'
              fullWidth
              disabled
              value={currentMonth.seatGrowth}
            />
          </Col>
          <Col md={6} xl={3} xxl={2}>
            <TextField
              label={t("periodChurnReport.incomeChurn")}
              className='mb-3'
              fullWidth
              disabled
              value={currentMonth.incomeChurn}
            />
            <TextField
              label={t("periodChurnReport.incomeGrowth")}
              className='mb-3'
              fullWidth
              disabled
              value={currentMonth.incomeGrowth}
            />
          </Col>
          <Col md={6} xl={3} xxl={2}>
            <TextField
              label={t("periodChurnReport.startSeats")}
              className='mb-3'
              fullWidth
              disabled
              value={currentMonth.startSeats}
            />
            <TextField
              label={t("periodChurnReport.endSeats")}
              className='mb-3'
              fullWidth
              disabled
              value={currentMonth.endSeats}
            />
          </Col>
          <Col md={6} xl={3} xxl={2}>
            <TextField
              label={t("periodChurnReport.startIncome")}
              className='mb-3'
              fullWidth
              disabled
              value={toCurrency(currentMonth.startIncome)}
            />
            <TextField
              label={t("periodChurnReport.endIncome")}
              className='mb-3'
              fullWidth
              disabled
              value={toCurrency(currentMonth.endIncome)}
            />
          </Col>
          <Col md={6} xl={3} xxl={2}>
            <TextField
              label={t("periodChurnReport.incomeDiff")}
              className='mb-3'
              fullWidth
              disabled
              value={toCurrency(currentMonth.incomeDiff)}
            />
            <TextField
              label={t("periodChurnReport.seatsDiff")}
              className='mb-3'
              fullWidth
              disabled
              value={currentMonth.seatsDiff}
            />
          </Col>
        </Row>
        }
        <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: '20px' }}>
          <Tabs
            value={selectedTab}
            variant="scrollable"
            scrollButtons="auto">
            <Tab label={t("standard.terminatedItems")} onClick={selectTerminatedItems} />
            <Tab label={t("standard.newItems")} onClick={selectNewItems} />
            <Tab label={t("standard.changedItems")} onClick={selectChangedItems} />
          </Tabs>
        </Box>
        <WebkuaTable
          headers={headers}
          data={selectedItems}
          tableConfig={tableConfig}
        />
      </>}
  </>;
}

export default PeriodChurnReport;